// Teacher navigation context. Tracks current page within the teacher dashboard.
import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

export type TeacherPage =
  | 'home'
  | 'liveActivity'
  | 'liveScreens'
  | 'activeAlerts'   // legacy key kept for backwards compat
  | 'alerts'
  | 'myClasses'
  | 'studentsMonitored' // legacy key kept for backwards compat
  | 'students'
  | 'reports'
  | 'notifications'
  | 'gamificationMVP'
  | 'settings'
  | 'profile'         // legacy key kept for backwards compat
  | 'documentation';

interface TeacherNavigationContextType {
  currentPage: TeacherPage;
  setCurrentPage: (page: TeacherPage) => void;
}

const TeacherNavigationContext = createContext<TeacherNavigationContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useTeacherNavigation = () => {
  const context = useContext(TeacherNavigationContext);
  if (!context) throw new Error('useTeacherNavigation must be used within TeacherNavigationProvider');
  return context;
};

interface TeacherNavigationProviderProps {
  children: ReactNode;
}

export const TeacherNavigationProvider = ({ children }: TeacherNavigationProviderProps) => {
  const [currentPage, setCurrentPage] = useState<TeacherPage>('home');

  // Normalise legacy page keys to new ones
  const navigate = (page: TeacherPage) => {
    const alias: Partial<Record<TeacherPage, TeacherPage>> = {
      activeAlerts: 'alerts',
      studentsMonitored: 'students',
      profile: 'settings',
    };
    setCurrentPage(alias[page] ?? page);
  };

  return (
    <TeacherNavigationContext.Provider value={{ currentPage, setCurrentPage: navigate }}>
      {children}
    </TeacherNavigationContext.Provider>
  );
};
